'use client';

import { useQuery } from '@tanstack/react-query';
import { flatten } from 'lodash-es';
import styled from 'styled-components';

import { AlarmAdd, CartBox, CheckOutlined, Shop } from '@zoomit/icons';
import { useMeasure } from '@zoomit/primitives';
import type { BaseImageType} from '@zoomit/web-kit';
import { PriceNotifyModal, usePriceNotify } from '@zoomit/web-kit';
import { Anchor, Button, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { getPriceChart } from '../../apis/product';
import { STORES_ID } from '../../constants/single-product';
import Chart from '../shared/Chart';
import RangePrice from '../shared/RangePrice';

type Props = {
  priceFrom: number;
  productId: string;
  priceTo: number;
  inStock: boolean;
  hasPriceChart: boolean;
  image: BaseImageType;
  title: string;
};

const PriceChart = (props: Props) => {
  const { priceFrom, productId, priceTo, inStock, hasPriceChart, image, title } = props;

  const { priceNotifyPopOverState, notifyQuery, deleteMutation, postMutation } = usePriceNotify({
    productId,
  });

  const {
    ref,
    rect: { width },
  } = useMeasure<HTMLDivElement>();

  const { data: priceChartData, isFetching } = useQuery(
    ['price-chart', `productId:${productId}`, `range:All`],
    async () => {
      const res = await getPriceChart(productId, 'All');
      return res.data;
    },
    { enabled: hasPriceChart },
  );

  const hasPriceChartData =
    priceChartData &&
    priceChartData.ticks &&
    Array.isArray(priceChartData.ticks) &&
    priceChartData.ticks.length > 0;

  const chartData = hasPriceChartData ? flatten(priceChartData.ticks) : [];

  return (
    <div className="border-grey6 xs:w-full xs:pt-4 rounded-sm border px-4 pt-10 pb-4 xl:w-[324px]">
      <div className="xs:hidden h-[112px] w-full items-center justify-center xl:flex" ref={ref}>
        {isFetching && <Spinner />}
        {!isFetching &&
          width > 0 &&
          (hasPriceChartData ? (
            <Chart
              id="summary-chart"
              width={width}
              height={112}
              data={chartData}
              xAccessor={(d) => new Date(d.date).getTime()}
              yAccessor={(d) => d.price}
            />
          ) : (
            <CartBox size="xxxl" stroke={cssVar(theme.grey5)} />
          ))}
      </div>
      <div className="bg-black5 xs:hidden mt-2 mb-6 h-px w-full xl:block" />
      {inStock ? (
        <div className="xs:flex-row flex items-center justify-between xl:flex-col">
          <RangePrice priceFrom={priceFrom} priceTo={priceTo} fontVariant="large" />
          <div className="xs:w-1/2 xl:mt-5 xl:w-full">
            <Anchor
              href={`#${STORES_ID}`}
              variant="contained"
              size="full width"
              fontWeight="bold"
              rightIcon={<Shop className="!fill-white" size="sm" />}
            >
              مشاهده فروشندگان
            </Anchor>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="xs:mt-0 xl:mt-14">
            <Typography
              tag="div"
              fontSize={1.4}
              fontWeight="bold"
              textColor={cssVar(theme.grey1)}
              textAlign="center"
            >
              این محصول فعلا فروشنده‌ای ندارد
            </Typography>
          </div>
          {notifyQuery.data ? (
            <ActionButton
              variant="text"
              size="fit"
              fontWeight="normal"
              rightIcon={<CheckOutlined className="!fill-success1" size="sm" />}
              onClick={() => deleteMutation.mutate()}
            >
              <Typography fontSize={1.2} textColor={cssVar(theme.success1)} fontWeight="bold">
                موجود شد خبرت می‌کنیم
              </Typography>
            </ActionButton>
          ) : (
            <ActionButton
              variant="text"
              size="fit"
              fontWeight="normal"
              rightIcon={<AlarmAdd className="!fill-text" size="sm" />}
              onClick={priceNotifyPopOverState.open}
            >
              <Typography fontSize={1.2} textColor={cssVar(theme.text)} fontWeight="bold">
                موجود شد خبرم کن
              </Typography>
            </ActionButton>
          )}
        </div>
      )}
      <PriceNotifyModal
        productId={productId}
        title={title}
        image={image}
        onClose={priceNotifyPopOverState.close}
        isPriceNotifyShow={priceNotifyPopOverState.isOpen}
        onSetNotify={() => postMutation.mutate()}
      />
    </div>
  );
};

export default PriceChart;

const ActionButton = styled(Button)`
  padding: 0;
  font-size: 13px;

  div {
    justify-content: flex-start;
  }
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border-radius: var(${theme.radius.full});
  border: 2px solid;
  border-color: var(${theme.grey5}) var(${theme.grey5}) var(${theme.black3});
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(359deg);
    }
  }
`;
